import React, { useMemo } from 'react';
import { IconButton, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const EmailSendTable = ({
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  loading,
  isFetching,
  onView,
  onRetry,
  onDelete,
  onEventOperation,
}) => {
  const columns = useMemo(() => ([
    {
      field: 'createdAt',
      headerName: 'Sent',
      minWidth: 170,
      valueGetter: (params) => params?.row?.createdAt ?? params?.row?.created_at ?? null,
      renderCell: (params) =>
        formatDateTime(params?.row?.createdAt ?? params?.row?.created_at ?? params?.value),
    },
    { field: 'status', headerName: 'Status', minWidth: 110 },
    { field: 'programName', headerName: 'Program', minWidth: 140 },
    { field: 'bac', headerName: 'BAC', minWidth: 90 },
    { field: 'triggerName', headerName: 'Trigger', minWidth: 180, flex: 1 },
    { field: 'templateName', headerName: 'Template', minWidth: 180, flex: 1 },
    {
      field: 'recipients',
      headerName: 'Recipients',
      minWidth: 120,
      valueGetter: (params) => {
        const value = params?.value ?? params?.row?.recipients;
        return Array.isArray(value) ? value.length : 0;
      },
    },
    { field: 'subjectRendered', headerName: 'Subject', minWidth: 240, flex: 1 },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      minWidth: 130,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            aria-label="View log"
            onClick={() => onView(params.row.id)}
          >
            <OpenInNewIcon fontSize="inherit" />
          </IconButton>
          {params.row.genericActions?.retry !== false && (
            <IconButton
              size="small"
              aria-label="Retry send"
              onClick={() => onRetry(params.row.id)}
            >
              <RefreshIcon fontSize="inherit" />
            </IconButton>
          )}
          {(params.row.eventOperations?.retry ||
            params.row.eventOperations?.resend) && (
            <IconButton
              size="small"
              aria-label={
                params.row.eventOperations?.resend
                  ? 'Resend event email'
                  : 'Retry event email'
              }
              onClick={() => onEventOperation(
                params.row,
                params.row.eventOperations?.resend ? 'resend' : 'retry'
              )}
            >
              <RefreshIcon fontSize="inherit" />
            </IconButton>
          )}
          {params.row.genericActions?.delete !== false && (
            <IconButton
              size="small"
              aria-label="Delete log"
              onClick={() => onDelete(params.row.id)}
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          )}
        </Stack>
      ),
    },
  ]), [onDelete, onEventOperation, onRetry, onView]);

  return (
    <DataGrid
      autoHeight
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={[25, 50, 100]}
      loading={loading || isFetching}
      disableRowSelectionOnClick
    />
  );
};

export default EmailSendTable;
